/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useCallback, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Checkbox,
  useCheckboxGroup,
  HStack,
  Center,
  Spinner,
  Text,
  Portal,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
} from '@chakra-ui/core';
import theme from '../../../../../styles/customTheme';

// Multi type for options
export type MultiOption<T = string> = { value: T; label: string };

// Option row for virtualized list
function OptionRow({ index, style, data }: any) {
  const { t } = useTranslation('moniterByRecruiters');

  const { options, getCheckboxProps, setValue } = data;
  const opt = options[index];

  return (
    <HStack
      style={style}
      position="relative"
      _hover={{
        backgroundColor: theme.colors.table.hoverBg,
        paddingRight: 2,
        '& > .only-action': {
          display: 'inline-block',
        },
      }}
      justify="space-between"
      data-testid={`multi-select-option-row-${opt.value}`}
    >
      <Checkbox
        px={4}
        height={30}
        w="100%"
        {...getCheckboxProps({ value: opt.value })}
        data-testid={`multi-select-option-${opt.value}`}
      >
        <Text fontSize="sm" lineHeight={1.2} noOfLines={2} isTruncated>
          {opt.label}
        </Text>
      </Checkbox>
      <Button
        size="xs"
        variant="outline"
        className="only-action"
        display="none"
        fontWeight="normal"
        px={2}
        minW="auto"
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          setValue([opt.value]);
        }}
        data-testid={`multi-select-only-button-${opt.value}`}
      >
        {t('only')}
      </Button>
    </HStack>
  );
}

interface MultiSelectProps<T> {
  options: MultiOption<T>[];
  selectedValues: T[];
  onChange: (values: T[], isAllSelected: boolean) => void;
  selectAll?: boolean;
  allSelectText?: string;
  label?: string;
  placeholder?: string;
  isLoading?: boolean;
  height?: number;
  itemHeight?: number;
  'data-testid'?: string;
  disabled?: boolean;
}

export function MultiSelect<T extends string | number>({
  options,
  selectedValues,
  onChange,
  selectAll = true,
  label = 'Select',
  placeholder = 'Search...',
  isLoading = false,
  height = 400,
  itemHeight = 30,
  allSelectText,
  'data-testid': dataTestId = 'multi-select',
  disabled = false,
}: MultiSelectProps<T>) {
  const { t } = useTranslation('candidateSearch');

  const maxHeight = Math.min(height, window.innerHeight * 0.5);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedOptions = useMemo(
    () => options.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase().trim())),
    [options, searchTerm],
  );

  const { value, setValue, getCheckboxProps } = useCheckboxGroup({
    value: selectedValues,
    onChange: (vals) => {
      // The `isAllSelected` flag should reflect if ALL original options are selected,
      // not just the currently filtered ones.
      const allOriginalOptionsSelected = vals.length === options.length && options.length > 0;
      onChange(vals as T[], allOriginalOptionsSelected);
    },
  });

  React.useEffect(() => {
    if (selectAll && options.length > 0 && selectedValues.length !== options.length) {
      onChange(
        options.map((o) => o.value),
        true,
      );
      setSearchTerm('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll, selectedValues, options]);

  const isAllSelected = useMemo(() => normalizedOptions.length > 0 && value.length === normalizedOptions.length, [
    value.length,
    normalizedOptions.length,
  ]);
  const isIndeterminate = useMemo(() => value.length > 0 && !isAllSelected, [value.length, isAllSelected]);

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setValue([]);
    } else {
      setValue(normalizedOptions.map((opt) => opt.value));
    }
  }, [isAllSelected, setValue, normalizedOptions]);

  const buttonContent = useMemo(() => {
    const allOriginalOptionsSelected = value.length === options.length && options.length > 0;

    if (allOriginalOptionsSelected || value.length === 0) {
      return (
        <Text fontSize="sm" data-testid="label">
          {allOriginalOptionsSelected && allSelectText ? allSelectText : label}
        </Text>
      );
    }
    return (
      <HStack spacing={2}>
        <Text fontSize="sm" data-testid="label">
          {label}
        </Text>
        <Text fontSize="sm" color="gray.600" data-testid={`${dataTestId}-selected-count`}>
          {`(${value.length})`}
        </Text>
      </HStack>
    );
  }, [value.length, options.length, label, dataTestId, allSelectText]);

  return (
    <Popover
      isLazy
      gutter={2}
      placement="bottom-start"
      isOpen={isOpen}
      onClose={
        // istanbul ignore next
        () => {
          if (selectAll && value.length === 0 && normalizedOptions.length > 0) {
            setValue(normalizedOptions.map((opt) => opt.value));
          }
          setIsOpen(false);
        }
      }
    >
      <PopoverTrigger>
        <Button
          isDisabled={isLoading || disabled}
          px={2}
          py={1}
          transition="all 0.2s"
          borderRadius="md"
          borderWidth="1px"
          variant="outline"
          size="sm"
          _focus={{ boxShadow: 'outline' }}
          flexGrow={1}
          fontWeight="normal"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((v) => !v);
          }}
          data-testid={`${dataTestId}-button`}
        >
          <HStack justify="space-between" width="-webkit-fill-available">
            {buttonContent}
            {isLoading ? <Spinner size="sm" data-testid={`${dataTestId}-spinner`} /> : <ChevronDownIcon />}
          </HStack>
        </Button>
      </PopoverTrigger>

      <Portal>
        <PopoverContent minWidth={300} maxWidth="fit-content" overflowY="auto" p={0} data-testid={`${dataTestId}-popover`}>
          {!isLoading && (
            <Box position="sticky" top={0} bg="white" zIndex={1} boxShadow={theme.shadows.md}>
              {normalizedOptions.length > 0 && normalizedOptions.length > 0 && (
                <Checkbox
                  px={4}
                  py={2}
                  w="100%"
                  isChecked={isAllSelected}
                  isIndeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  data-testid={`${dataTestId}-select-all`}
                >
                  <Text fontSize="sm" lineHeight={1}>
                    {t('filters.selectAll')}
                  </Text>
                </Checkbox>
              )}

              <Input
                type="search"
                placeholder={placeholder}
                borderLeftColor="transparent"
                borderRight="transparent"
                _focus={{
                  outline: '2px solid blue',
                  outlineOffset: '-2px',
                }}
                _placeholder={{
                  fontSize: 'sm',
                }}
                borderRadius={0}
                value={searchTerm}
                onChange={
                  // istanbul ignore next
                  (e) => setSearchTerm(e.target.value)
                }
                data-testid={`${dataTestId}-search-input`}
              />
            </Box>
          )}

          {!isLoading && normalizedOptions.length > 0 && (
            <List
              height={maxHeight}
              itemCount={normalizedOptions.length}
              itemSize={itemHeight}
              width="100%"
              itemData={{ options: normalizedOptions, getCheckboxProps, setValue }}
            >
              {OptionRow}
            </List>
          )}

          {!isLoading && normalizedOptions.length === 0 && (
            <Center py={3} data-testid={`${dataTestId}-no-options`}>
              <Text fontSize="sm" color="gray.500">
                No options available
              </Text>
            </Center>
          )}
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
